import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import LoginPage from '@/pages/LoginPage';
import DashboardLayout from '@/components/DashboardLayout';
import Dashboard from '@/pages/Dashboard';
import Customers from '@/pages/Customers';
import Tickets from '@/pages/Tickets';
import CreateTicket from '@/pages/CreateTicket';
import TicketDetails from '@/pages/TicketDetails';
import RateManagement from '@/pages/RateManagement';
import BillingHistory from '@/pages/BillingHistory';
import TagView from '@/pages/TagView';
import Analytics from '@/pages/Analytics';
import AdminDashboard from '@/pages/AdminDashboard';
import ManageStores from '@/pages/ManageStores';
import ManageUsers from '@/pages/ManageUsers';
import ManagePGs from '@/pages/ManagePGs';
import ManageOrders from '@/pages/ManageOrders';
import AdminChats from '@/pages/AdminChats';
import PGAnalytics from '@/pages/PGAnalytics';
import LandingPage from '@/pages/LandingPage';
import PricingPage from '@/pages/PricingPage';
import CustomerAuth from '@/pages/CustomerAuth';
import CustomerDashboard from '@/pages/CustomerDashboard';
import CustomerNewOrder from '@/pages/CustomerNewOrder';
import CustomerOrders from '@/pages/CustomerOrders';
import CustomerNotifications from '@/pages/CustomerNotifications';
import CustomerChat from '@/pages/CustomerChat';
import CustomerProfile from '@/pages/CustomerProfile';
import IndividualAuth from '@/pages/IndividualAuth';
import IndividualDashboard from '@/pages/IndividualDashboard';
import IndividualOrders from '@/pages/IndividualOrders';
import IndividualNewOrder from '@/pages/IndividualNewOrder';
import IndividualFoldPass from '@/pages/IndividualFoldPass';
import IndividualChat from '@/pages/IndividualChat';
import IndividualProfile from '@/pages/IndividualProfile';
import IndividualReferrals from '@/pages/IndividualReferrals';
import IndividualNotifications from '@/pages/IndividualNotifications';
import AdminIndividualCustomers from '@/pages/AdminIndividualCustomers';
import AdminIndividualOrders from '@/pages/AdminIndividualOrders';
import AdminCoupons from '@/pages/AdminCoupons';
import AdminRatings from '@/pages/AdminRatings';
import AdminDailyOrders from '@/pages/AdminDailyOrders';
import AdminDeliveryTracking from '@/pages/AdminDeliveryTracking';
import AdminDeliveryPerformance from '@/pages/AdminDeliveryPerformance';
import AdminIndividualAnalytics from '@/pages/AdminIndividualAnalytics';
import AdminSlotManagement from '@/pages/AdminSlotManagement';
import DeliveryDashboard from '@/pages/DeliveryDashboard';
import ManageDeliveryBoys from '@/pages/ManageDeliveryBoys';
import AdminPriceDashboard from '@/pages/AdminPriceDashboard';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import '@/App.css';

const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page - First Screen */}
      <Route path="/" element={<LandingPage />} />

      {/* Admin Login */}
      <Route path="/login" element={<LoginPage />} />

      {/* Delivery Boy Portal */}
      <Route path="/delivery/dashboard" element={<DeliveryDashboard />} />

      {/* Pricing Page */}
      <Route path="/pricing" element={<PricingPage />} />

      {/* Individual Customer Routes */}
      <Route path="/individual/login" element={<IndividualAuth />} />
      <Route path="/individual/dashboard" element={<IndividualDashboard />} />
      <Route path="/individual/new-order" element={<IndividualNewOrder />} />
      <Route path="/individual/foldpass" element={<IndividualFoldPass />} />
      <Route path="/individual/orders" element={<IndividualOrders />} />
      <Route path="/individual/chat" element={<IndividualChat />} />
      <Route path="/individual/profile" element={<IndividualProfile />} />
      <Route path="/individual/referrals" element={<IndividualReferrals />} />
      <Route path="/individual/notifications" element={<IndividualNotifications />} />

      {/* Customer Routes */}
      <Route path="/customer/login" element={<CustomerAuth />} />
      <Route path="/customer/dashboard" element={<CustomerDashboard />} />
      <Route path="/customer/new-order" element={<CustomerNewOrder />} />
      <Route path="/customer/orders" element={<CustomerOrders />} />
      <Route path="/customer/notifications" element={<CustomerNotifications />} />
      <Route path="/customer/chat" element={<CustomerChat />} />
      <Route path="/customer/profile" element={<CustomerProfile />} />

      {/* Admin Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="customers" element={<Customers />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="tickets/create" element={<CreateTicket />} />
        <Route path="tickets/:ticketId" element={<TicketDetails />} />
        <Route path="tickets/:ticketId/tag" element={<TagView />} />
        <Route path="rates" element={<RateManagement />} />
        <Route path="billing" element={<BillingHistory />} />
        <Route path="analytics" element={<Analytics />} />

        {/* Admin Routes */}
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="admin/stores" element={<ManageStores />} />
        <Route path="admin/users" element={<ManageUsers />} />
        <Route path="admin/pgs" element={<ManagePGs />} />
        <Route path="admin/orders" element={<ManageOrders />} />
        <Route path="admin/chats" element={<AdminChats />} />
        <Route path="admin/analytics" element={<PGAnalytics />} />
        <Route path="admin/individual-customers" element={<AdminIndividualCustomers />} />
        <Route path="admin/individual-orders" element={<AdminIndividualOrders />} />
        <Route path="admin/coupons" element={<AdminCoupons />} />
        <Route path="admin/ratings" element={<AdminRatings />} />
        <Route path="admin/individual-analytics" element={<AdminIndividualAnalytics />} />
        <Route path="admin/slot-management" element={<AdminSlotManagement />} />
        <Route path="admin/delivery-boys" element={<ManageDeliveryBoys />} />
        <Route path="admin/price-dashboard" element={<AdminPriceDashboard />} />
        <Route path="admin/daily-orders" element={<AdminDailyOrders />} />
        <Route path="admin/delivery-tracking" element={<AdminDeliveryTracking />} />
        <Route path="admin/delivery-performance" element={<AdminDeliveryPerformance />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
